/**
 * Preset Service
 * Hybrid local/cloud preset storage with offline-first strategy
 *
 * - Cloud storage for authenticated (non-anonymous) users when online
 * - localStorage fallback for offline mode or anonymous users
 * - Sync queue for pending operations when offline
 * - Conflict resolution using version timestamps (higher wins)
 */

import { authService } from '../auth/auth-service.js';
import { entitlementService } from '../entitlements/entitlement-service.js';

const LOCAL_PRESETS_KEY = 'audioVisualizerPresets';
const PENDING_SYNC_KEY = 'audioVisualizerPresets_pendingSync';
const LAST_SYNC_KEY = 'audioVisualizerPresets_lastSync';
const MIGRATED_KEY = 'audioVisualizerPresets_migrated_';

// Custom error classes
export class PresetLimitError extends Error {
  constructor(message, limit, current) {
    super(message);
    this.name = 'PresetLimitError';
    this.limit = limit;
    this.current = current;
  }
}

export class SyncError extends Error {
  constructor(message, retryable = true) {
    super(message);
    this.name = 'SyncError';
    this.retryable = retryable;
  }
}

class PresetService {
  constructor() {
    this.supabase = null;
    this.syncInProgress = false;
    this.listeners = new Set();
    this.onlineHandler = null;
    this.authUnsubscribe = null;
    this.initialized = false;
    this.syncStatus = { syncing: false, lastSync: null, error: null };
  }

  /**
   * Initialize service - call after auth is ready
   */
  async init() {
    if (this.initialized) return;

    this.supabase = authService.getClient();

    // Listen for online/offline events
    this.onlineHandler = () => this.handleOnline();
    window.addEventListener('online', this.onlineHandler);

    // Listen for auth state changes (for first-time login migration)
    this.authUnsubscribe = authService.onAuthStateChange(
      (event, session) => this.handleAuthChange(event, session)
    );

    // Initial sync if online and authenticated
    if (navigator.onLine && this.shouldUseCloud()) {
      await this.syncPresets();
    }

    this.initialized = true;
    console.log('[PresetService] Initialized');
  }

  /**
   * Cleanup
   */
  dispose() {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
    }
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
    this.listeners.clear();
  }

  /**
   * Check if cloud storage should be used
   */
  shouldUseCloud() {
    return authService.isAuthenticated() &&
           !authService.isAnonymous() &&
           this.supabase;
  }

  // ============================================
  // Local Storage Operations
  // ============================================

  /**
   * Get presets from localStorage
   */
  getLocalPresets() {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_PRESETS_KEY)) || {};
    } catch {
      return {};
    }
  }

  /**
   * Save presets to localStorage
   */
  setLocalPresets(presets) {
    localStorage.setItem(LOCAL_PRESETS_KEY, JSON.stringify(presets));
  }

  /**
   * Save a single preset to localStorage
   */
  saveLocalPreset(name, preset) {
    const presets = this.getLocalPresets();
    presets[name] = preset;
    this.setLocalPresets(presets);
  }

  /**
   * Delete a preset from localStorage
   */
  deleteLocalPreset(name) {
    const presets = this.getLocalPresets();
    delete presets[name];
    this.setLocalPresets(presets);
  }

  // ============================================
  // Cloud Storage Operations
  // ============================================

  /**
   * Fetch all presets from Supabase
   */
  async fetchCloudPresets(retries = 3) {
    if (!this.supabase) return {};

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const { data, error } = await this.supabase
          .from('user_presets')
          .select('*')
          .order('updated_at', { ascending: false });

        if (error) throw error;

        // Transform to local format
        const presets = {};
        for (const row of data || []) {
          presets[row.name] = {
            visualizationType: row.visualizer_type,
            config: row.config,
            version: row.version,
            cloudId: row.id,
            updatedAt: row.updated_at
          };
        }
        return presets;
      } catch (error) {
        if (attempt === retries) throw error;
        // Exponential backoff: 1s, 2s, 4s
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
      }
    }
    return {};
  }

  /**
   * Save a preset to Supabase
   */
  async saveCloudPreset(preset) {
    if (!this.supabase) throw new Error('Supabase not initialized');

    const userId = authService.getUser()?.id;
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await this.supabase
      .from('user_presets')
      .upsert({
        user_id: userId,
        name: preset.name,
        visualizer_type: preset.visualizerType,
        config: preset.config,
        version: preset.version
      }, {
        onConflict: 'user_id,name'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Delete a preset from Supabase
   */
  async deleteCloudPreset(name) {
    if (!this.supabase) throw new Error('Supabase not initialized');

    const userId = authService.getUser()?.id;
    if (!userId) throw new Error('Not authenticated');

    const { error } = await this.supabase
      .from('user_presets')
      .delete()
      .eq('user_id', userId)
      .eq('name', name);

    if (error) throw error;
  }

  /**
   * Get count of user's cloud presets
   */
  async getCloudPresetCount() {
    if (!this.supabase) return 0;

    const userId = authService.getUser()?.id;
    if (!userId) return 0;

    const { count, error } = await this.supabase
      .from('user_presets')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (error) {
      console.warn('[PresetService] Error getting preset count:', error);
      return 0;
    }

    return count || 0;
  }

  // ============================================
  // Sync Queue Operations
  // ============================================

  /**
   * Get pending sync queue
   */
  getPendingSync() {
    try {
      return JSON.parse(localStorage.getItem(PENDING_SYNC_KEY)) || { queue: [] };
    } catch {
      return { queue: [] };
    }
  }

  /**
   * Save pending sync queue
   */
  setPendingSync(pending) {
    localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(pending));
  }

  /**
   * Add operation to pending sync queue
   */
  addToPendingSync(action, presetName, data = null) {
    const pending = this.getPendingSync();

    // Remove any existing operation for this preset (latest wins)
    pending.queue = pending.queue.filter(op => op.presetName !== presetName);

    pending.queue.push({
      action,
      presetName,
      data,
      timestamp: Date.now()
    });

    this.setPendingSync(pending);
  }

  /**
   * Process pending sync queue
   */
  async processPendingSync() {
    const pending = this.getPendingSync();
    if (pending.queue.length === 0) return;

    const remaining = [];

    for (const op of pending.queue) {
      try {
        if (op.action === 'upsert' && op.data) {
          await this.saveCloudPreset({
            name: op.presetName,
            visualizerType: op.data.visualizationType,
            config: op.data.config,
            version: op.data.version || Date.now()
          });
        } else if (op.action === 'delete') {
          await this.deleteCloudPreset(op.presetName);
        }
      } catch (error) {
        console.warn(`[PresetService] Failed to sync ${op.action} for "${op.presetName}":`, error);
        remaining.push(op);
      }
    }

    this.setPendingSync({ queue: remaining });
  }

  // ============================================
  // Public API
  // ============================================

  /**
   * Get all presets (merged local + cloud)
   */
  async getPresets() {
    // Always start with localStorage (offline-first)
    const localPresets = this.getLocalPresets();

    if (!this.shouldUseCloud() || !navigator.onLine) {
      return localPresets;
    }

    try {
      const cloudPresets = await this.fetchCloudPresets();
      // Merge: cloud takes precedence (higher version wins)
      const merged = this.mergePresets(localPresets, cloudPresets);
      // Update local storage with merged result
      this.setLocalPresets(merged);
      return merged;
    } catch (error) {
      console.warn('[PresetService] Cloud fetch failed, using local:', error);
      return localPresets;
    }
  }

  /**
   * Save a preset
   */
  async savePreset(name, data) {
    // Check tier limit for cloud storage
    if (this.shouldUseCloud()) {
      const canSave = await this.checkPresetLimit();
      if (!canSave.allowed) {
        throw new PresetLimitError(canSave.message, canSave.limit, canSave.current);
      }
    }

    const preset = {
      visualizationType: data.visualizationType,
      config: data.config,
      version: Date.now(),
      updatedAt: new Date().toISOString()
    };

    // Always save locally first
    this.saveLocalPreset(name, preset);

    // Queue for cloud sync
    if (this.shouldUseCloud()) {
      if (navigator.onLine) {
        try {
          await this.saveCloudPreset({
            name,
            visualizerType: preset.visualizationType,
            config: preset.config,
            version: preset.version
          });
          this.updateSyncStatus({ syncing: false, lastSync: Date.now(), error: null });
        } catch (error) {
          this.addToPendingSync('upsert', name, preset);
          this.updateSyncStatus({ syncing: false, error: 'Saved locally, will sync when online' });
          console.warn('[PresetService] Cloud save failed, queued for sync:', error);
        }
      } else {
        this.addToPendingSync('upsert', name, preset);
      }
    }

    this.notifyListeners();
    return preset;
  }

  /**
   * Delete a preset
   */
  async deletePreset(name) {
    // Delete from local storage
    this.deleteLocalPreset(name);

    // Queue for cloud sync
    if (this.shouldUseCloud()) {
      if (navigator.onLine) {
        try {
          await this.deleteCloudPreset(name);
          this.updateSyncStatus({ syncing: false, lastSync: Date.now(), error: null });
        } catch (error) {
          this.addToPendingSync('delete', name);
          console.warn('[PresetService] Cloud delete failed, queued for sync:', error);
        }
      } else {
        this.addToPendingSync('delete', name);
      }
    }

    this.notifyListeners();
  }

  /**
   * Check if user can save more presets
   */
  async checkPresetLimit() {
    if (!this.shouldUseCloud()) {
      return { allowed: true };
    }

    try {
      const entitlements = await entitlementService.getEntitlements();
      const maxPresets = entitlements.features?.maxPresets;

      // -1 means unlimited
      if (maxPresets === -1 || maxPresets === undefined) {
        return { allowed: true };
      }

      const currentCount = await this.getCloudPresetCount();

      if (currentCount >= maxPresets) {
        return {
          allowed: false,
          message: `You've reached your limit of ${maxPresets} presets. Upgrade to save more.`,
          limit: maxPresets,
          current: currentCount
        };
      }

      return { allowed: true, limit: maxPresets, current: currentCount };
    } catch (error) {
      console.warn('[PresetService] Error checking preset limit:', error);
      // Allow on error to not block user
      return { allowed: true };
    }
  }

  /**
   * Sync presets with cloud
   */
  async syncPresets() {
    if (this.syncInProgress || !this.shouldUseCloud()) return;

    this.syncInProgress = true;
    this.updateSyncStatus({ syncing: true });

    try {
      // 1. Process pending sync queue
      await this.processPendingSync();

      // 2. Fetch cloud presets
      const cloudPresets = await this.fetchCloudPresets();

      // 3. Fetch local presets
      const localPresets = this.getLocalPresets();

      // 4. Merge using version numbers (higher wins)
      const merged = this.mergePresets(localPresets, cloudPresets);

      // 5. Update local storage with merged result
      this.setLocalPresets(merged);

      // 6. Update last sync timestamp
      const lastSync = Date.now();
      localStorage.setItem(LAST_SYNC_KEY, lastSync.toString());

      this.updateSyncStatus({ syncing: false, lastSync, error: null });
      this.notifyListeners();
    } catch (error) {
      console.error('[PresetService] Sync failed:', error);
      this.updateSyncStatus({ syncing: false, error: error.message });
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Merge local and cloud presets
   * Cloud presets with higher version win
   */
  mergePresets(local, cloud) {
    const merged = {};
    const allNames = new Set([...Object.keys(local), ...Object.keys(cloud)]);

    for (const name of allNames) {
      const localPreset = local[name];
      const cloudPreset = cloud[name];

      if (!localPreset) {
        merged[name] = cloudPreset;
      } else if (!cloudPreset) {
        merged[name] = localPreset;
      } else {
        // Both exist: higher version wins
        const localVersion = localPreset.version || 0;
        const cloudVersion = cloudPreset.version || 0;
        merged[name] = localVersion > cloudVersion ? localPreset : cloudPreset;
      }
    }

    return merged;
  }

  /**
   * Get sync status
   */
  getSyncStatus() {
    return { ...this.syncStatus };
  }

  /**
   * Update sync status and notify listeners
   */
  updateSyncStatus(status) {
    this.syncStatus = { ...this.syncStatus, ...status };
    this.notifyListeners();
  }

  /**
   * Subscribe to preset/sync changes
   */
  onPresetsChange(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.syncStatus);
      } catch (error) {
        console.error('[PresetService] Listener error:', error);
      }
    });
  }

  // ============================================
  // Event Handlers
  // ============================================

  /**
   * Handle coming back online
   */
  async handleOnline() {
    if (this.shouldUseCloud()) {
      console.log('[PresetService] Back online, syncing presets...');
      await this.syncPresets();
    }
  }

  /**
   * Handle auth state changes
   */
  async handleAuthChange(event, session) {
    // Update supabase client reference
    this.supabase = authService.getClient();

    if (event === 'SIGNED_IN' && session?.user && !session.user.is_anonymous) {
      // Check if this is a first-time migration
      const migratedKey = MIGRATED_KEY + session.user.id;
      const migrated = localStorage.getItem(migratedKey);

      if (!migrated) {
        await this.migrateLocalPresetsToCloud();
        localStorage.setItem(migratedKey, 'true');
      }

      // Always sync on login
      await this.syncPresets();
    }
  }

  /**
   * Migrate existing localStorage presets to cloud
   * Called on first authenticated login
   */
  async migrateLocalPresetsToCloud() {
    const localPresets = this.getLocalPresets();
    const presetNames = Object.keys(localPresets);

    if (presetNames.length === 0) return;

    console.log(`[PresetService] Migrating ${presetNames.length} local presets to cloud...`);

    // Check limit
    let maxPresets;
    try {
      const entitlements = await entitlementService.getEntitlements();
      maxPresets = entitlements.features?.maxPresets || 10;
    } catch {
      maxPresets = 10; // Default fallback
    }

    // If unlimited, migrate all
    const limit = maxPresets === -1 ? presetNames.length : maxPresets;
    const presetsToMigrate = presetNames.slice(0, limit);

    let migratedCount = 0;
    for (const name of presetsToMigrate) {
      const preset = localPresets[name];
      try {
        await this.saveCloudPreset({
          name,
          visualizerType: preset.visualizationType,
          config: preset.config,
          version: preset.version || Date.now()
        });
        migratedCount++;
      } catch (error) {
        console.warn(`[PresetService] Failed to migrate preset "${name}":`, error);
      }
    }

    console.log(`[PresetService] Migrated ${migratedCount}/${presetNames.length} presets`);

    if (presetsToMigrate.length < presetNames.length) {
      console.info(`[PresetService] Some presets not migrated due to tier limit (${maxPresets})`);
    }
  }
}

// Export singleton instance
export const presetService = new PresetService();
